import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'

interface ActivityLog {
  id: number
  user_id: number | null
  user_username: string | null
  user_full_name: string | null
  client_id: number | null
  client_name: string | null
  action: string
  resource_type: string
  resource_id: number | null
  description: string
  ip_address: string | null
  user_agent: string | null
  request_method: string | null
  request_path: string | null
  request_data: any
  response_status: number | null
  created_at: string
}

interface ActivityLogsPageProps {
  token: string
}

interface Filters {
  user_id: string
  client_id: string
  action: string
  resource_type: string
  date_from: string
  date_to: string
  search: string
}

interface Stats {
  period_days: number
  total_activities: number
  actions: Array<{ action: string; count: number }>
  resources: Array<{ resource_type: string; count: number }>
  top_users: Array<{ username: string; full_name: string; count: number }>
  top_clients: Array<{ client_name: string; count: number }>
}

export default function ActivityLogsPage({ token }: ActivityLogsPageProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [limit] = useState(50)
  const [showStats, setShowStats] = useState(false)
  const [stats, setStats] = useState<Stats | null>(null)
  const [availableActions, setAvailableActions] = useState<string[]>([])
  const [availableResourceTypes, setAvailableResourceTypes] = useState<string[]>([])
  const [expandedLog, setExpandedLog] = useState<number | null>(null)

  const [filters, setFilters] = useState<Filters>({
    user_id: '',
    client_id: '',
    action: '',
    resource_type: '',
    date_from: '',
    date_to: '',
    search: ''
  })

  useEffect(() => {
    fetchLogs()
    fetchAvailableFilters()
  }, [page, filters])

  const fetchLogs = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      })

      const response = await fetch(`/api/activity-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setLogs(data)
        
        // Pobierz liczbę wszystkich logów
        const countResponse = await fetch(`/api/activity-logs/count?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
        
        if (countResponse.ok) {
          const countData = await countResponse.json()
          setTotalCount(countData.count)
        }
      } else {
        setError('Błąd podczas pobierania logów aktywności')
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableFilters = async () => {
    try {
      const [actionsResponse, resourceTypesResponse] = await Promise.all([
        fetch('/api/activity-logs/actions', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/activity-logs/resource-types', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      if (actionsResponse.ok) {
        const actions = await actionsResponse.json()
        setAvailableActions(actions)
      }

      if (resourceTypesResponse.ok) {
        const resourceTypes = await resourceTypesResponse.json()
        setAvailableResourceTypes(resourceTypes)
      }
    } catch (err) {
      console.error('Błąd podczas pobierania filtrów:', err)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/activity-logs/stats?days=30', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (err) {
      console.error('Błąd podczas pobierania statystyk:', err)
    }
  }

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1) // Reset do pierwszej strony przy zmianie filtrów
  }

  const clearFilters = () => {
    setFilters({
      user_id: '',
      client_id: '',
      action: '',
      resource_type: '',
      date_from: '',
      date_to: '',
      search: ''
    })
    setPage(1)
  }

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800'
      case 'UPDATE': return 'bg-blue-100 text-blue-800'
      case 'DELETE': return 'bg-red-100 text-red-800'
      case 'LOGIN_SUCCESS': return 'bg-green-100 text-green-800'
      case 'LOGIN_FAILED': return 'bg-red-100 text-red-800'
      case 'LOGOUT': return 'bg-gray-100 text-gray-800'
      case 'VIEW': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusBadgeColor = (status: number | null) => {
    if (!status) return 'bg-gray-100 text-gray-800'
    if (status >= 200 && status < 300) return 'bg-green-100 text-green-800'
    if (status >= 400 && status < 500) return 'bg-yellow-100 text-yellow-800'
    if (status >= 500) return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const totalPages = Math.ceil(totalCount / limit)

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Ładowanie logów aktywności...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Logi Aktywności</h1>
        <p className="text-gray-600">
          Przeglądaj wszystkie aktywności użytkowników w systemie MACRA Nexus
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Statystyki */}
      <div className="mb-6">
        <button
          onClick={() => {
            setShowStats(!showStats)
            if (!showStats && !stats) {
              fetchStats()
            }
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          {showStats ? 'Ukryj statystyki' : 'Pokaż statystyki'}
        </button>

        {showStats && stats && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ogółem aktywności</h3>
              <p className="text-3xl font-bold text-blue-600">{stats.total_activities}</p>
              <p className="text-sm text-gray-500">w ciągu ostatnich {stats.period_days} dni</p>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Top akcje</h3>
              <div className="space-y-1">
                {stats.actions.slice(0, 3).map((action, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{action.action}</span>
                    <span className="font-medium">{action.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Top zasoby</h3>
              <div className="space-y-1">
                {stats.resources.slice(0, 3).map((resource, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{resource.resource_type}</span>
                    <span className="font-medium">{resource.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Najaktywniejszi użytkownicy</h3>
              <div className="space-y-1">
                {stats.top_users.slice(0, 3).map((user, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{user.username}</span>
                    <span className="font-medium">{user.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtry */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtry</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Akcja
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Wszystkie akcje</option>
              {availableActions.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Typ zasobu
            </label>
            <select
              value={filters.resource_type}
              onChange={(e) => handleFilterChange('resource_type', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Wszystkie typy</option>
              {availableResourceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data od
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data do
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Wyszukaj
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Szukaj w opisie, ścieżce, IP..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Wyczyść filtry
            </button>
          </div>
        </div>
      </div>

      {/* Lista logów */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Logi aktywności ({totalCount} rekordów)
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data/Czas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Użytkownik
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akcja
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zasób
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: pl })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.user_username ? (
                        <div>
                          <div className="font-medium">{log.user_username}</div>
                          {log.user_full_name && (
                            <div className="text-gray-500">{log.user_full_name}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">SYSTEM</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionBadgeColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{log.resource_type}</div>
                      {log.resource_id && (
                        <div className="text-gray-500">ID: {log.resource_id}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {log.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.response_status && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(log.response_status)}`}>
                          {log.response_status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip_address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {expandedLog === log.id ? 'Ukryj' : 'Szczegóły'}
                      </button>
                    </td>
                  </tr>
                  {expandedLog === log.id && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 bg-gray-50">
                        <div className="space-y-2 text-sm">
                          {log.client_name && (
                            <div><strong>Referat:</strong> {log.client_name}</div>
                          )}
                          {log.request_method && log.request_path && (
                            <div><strong>Żądanie:</strong> {log.request_method} {log.request_path}</div>
                          )}
                          {log.user_agent && (
                            <div><strong>User Agent:</strong> {log.user_agent}</div>
                          )}
                          {log.request_data && (
                            <div>
                              <strong>Dane żądania:</strong>
                              <pre className="mt-1 bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                                {JSON.stringify(log.request_data, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginacja */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Strona {page} z {totalPages} (łącznie {totalCount} rekordów)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Poprzednia
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Następna
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
